import { Link } from "react-router-dom";
import {
  Radar, Clock, MapPin, Building2, TrendingUp, ArrowLeft,
} from "lucide-react";
import { deriveDaysAgo } from "@/utils/opportunityDerived";
import { useOpportunities } from "@/hooks/useOpportunities";

/**
 * Investment Dashboard — the executive KPI strip.
 *
 * Sits toward the bottom of the Home page (per user IA) as a
 * "state-of-the-week" summary of what's active in the platform right now.
 * Uses restrained hairline cards + mono tabular numerals for the metric.
 */
export const DashboardStrip = () => {
  const { opportunities, countries, industries } = useOpportunities();

  const today = opportunities.filter((o) => deriveDaysAgo(o.publishedAt) === 0).length;
  const thisWeek = opportunities.filter((o) => deriveDaysAgo(o.publishedAt) <= 7).length;
  const urgent = opportunities.filter(
    (o) => (o.meta || {}).timeSensitivity === "urgent",
  ).length;
  const activeCountries = new Set(opportunities.map((o) => o.country)).size;
  const activeIndustries = new Set(opportunities.map((o) => o.industry)).size;

  const cards = [
    { key: "today",      label: "فرص اليوم",     value: today,           icon: Radar,      to: "/opportunities?time=today" },
    { key: "week",       label: "هذا الأسبوع",   value: thisWeek,        icon: Clock,      to: "/opportunities?time=week" },
    { key: "urgent",     label: "عاجلة",          value: urgent,          icon: TrendingUp, to: "/opportunities?time=urgent", accent: true },
    { key: "countries",  label: `من ${countries.length} دولة`, value: activeCountries, icon: MapPin,     to: "/#countries" },
    { key: "industries", label: `من ${industries.length} قطاع`, value: activeIndustries, icon: Building2, to: "/#industries" },
  ];

  return (
    <section
      id="dashboard"
      data-testid="dashboard-strip"
      className="bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="fursa-eyebrow">Investment Dashboard</p>
            <h2 className="fursa-section-title mt-3 font-semibold tracking-tight text-ink-900">
              ما الذي يستحقّ انتباهك اليوم؟
            </h2>
          </div>
          <Link
            to="/opportunities"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-fursa-700 transition-colors hover:text-fursa-800"
          >
            جميع الفرص
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.key}
                to={c.to}
                data-testid={`dashboard-metric-${c.key}`}
                className={`group relative overflow-hidden rounded-xl border bg-white p-5 shadow-card transition-all duration-300 ease-out-expo hover:-translate-y-1 hover:shadow-card-hover ${
                  c.accent
                    ? "border-gold-200 hover:border-gold-400"
                    : "border-surface-200 hover:border-fursa-300"
                }`}
              >
                {c.accent && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -end-6 -top-6 h-16 w-16 rounded-full bg-gold-100 opacity-40 blur-xl transition-opacity group-hover:opacity-60"
                  />
                )}
                <div className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest ${
                  c.accent ? "text-gold-700" : "text-ink-500"
                }`}>
                  <Icon className={`h-3 w-3 ${c.accent ? "text-gold-600" : "text-intel-700"}`} strokeWidth={2} aria-hidden />
                  {c.label}
                </div>
                <div
                  dir="ltr"
                  className={`num relative mt-3 font-mono text-[32px] font-bold leading-none tabular-nums tracking-tightest ${
                    c.accent ? "text-gold-700" : "text-ink-900"
                  }`}
                >
                  {c.value}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
